import React, { useState } from 'react';
import { Star } from 'lucide-react';

/*
=====================================================================================
surveyShared.jsx
=====================================================================================
Common building blocks shared by CourseExperienceSurvey and UserOnboardingSurvey:
  - StarRating   : 1-5 star input
  - StepDots     : progress dots for multi-step modals
  - styles       : shared inline style object (same look & feel as before)
=====================================================================================
*/

export const StarRating = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= (hovered || value);
        return (
          <button
            key={n}
            type="button"
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            onClick={() => onChange(n)}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
            }}
          >
            <Star
              size={36}
              color={filled ? '#d97706' : '#d1d5db'}
              fill={filled ? '#d97706' : 'none'}
              style={{ transition: 'color 0.15s ease, fill 0.15s ease' }}
            />
          </button>
        );
      })}
    </div>
  );
};

export const StepDots = ({ steps, activeIndex }) => (
  <div style={styles.stepDots}>
    {steps.map((s, i) => (
      <div
        key={s.key}
        style={{
          ...styles.stepDot,
          ...(i === activeIndex ? styles.stepDotActive : {}),
          ...(i < activeIndex ? styles.stepDotDone : {}),
        }}
      />
    ))}
  </div>
);

export const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(10,10,20,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
    padding: 16,
    boxSizing: 'border-box',
  },
  modal: {
    width: '100%',
    maxHeight: '90vh',
    backgroundColor: '#ffffff',
    borderRadius: 20,
    boxShadow: '0 24px 48px rgba(10,10,20,0.25)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: '24px 28px 0',
  },
  headerEyebrow: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#7c3aed',
    marginBottom: 4,
  },
  headerTitle: {
    fontSize: 'clamp(18px, 4vw, 22px)',
    fontWeight: 700,
    color: '#131318',
    margin: 0,
    fontFamily: "'Space Grotesk', sans-serif",
    overflowWrap: 'anywhere',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 8,
    flexShrink: 0,
  },
  stepDots: {
    display: 'flex',
    gap: 6,
    padding: '18px 28px 0',
  },
  stepDot: {
    height: 4,
    flex: 1,
    borderRadius: 4,
    backgroundColor: '#e5e5ea',
  },
  stepDotActive: {
    backgroundColor: '#7c3aed',
  },
  stepDotDone: {
    backgroundColor: '#c4b5fd',
  },
  body: {
    padding: '24px 28px',
    overflowY: 'auto',
    flex: 1,
  },
  stepTitle: {
    fontSize: 'clamp(17px, 4vw, 20px)',
    fontWeight: 700,
    color: '#131318',
    margin: '0 0 6px',
    fontFamily: "'Space Grotesk', sans-serif",
  },
  stepSubtitle: {
    fontSize: 14,
    color: '#5c5d70',
    lineHeight: 1.5,
    margin: 0,
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 14,
    fontFamily: 'inherit',
    border: '1px solid rgba(10,10,20,0.16)',
    borderRadius: 10,
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  textInput: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 14,
    fontFamily: 'inherit',
    border: '1px solid rgba(10,10,20,0.16)',
    borderRadius: 10,
    outline: 'none',
    boxSizing: 'border-box',
    marginTop: 12,
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginTop: 16,
  },
  radioOption: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    backgroundColor: '#f1f2f6',
    borderRadius: 10,
    cursor: 'pointer',
    border: '2px solid transparent',
    fontSize: 14,
    color: '#131318',
  },
  radioOptionSelected: {
    borderColor: '#7c3aed',
    backgroundColor: 'rgba(124,58,237,0.08)',
  },
  radioInput: {
    width: 16,
    height: 16,
    accentColor: '#7c3aed',
    flexShrink: 0,
  },
  topicsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 16,
  },
  topicChip: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    borderRadius: 999,
    border: '1px solid rgba(10,10,20,0.16)',
    backgroundColor: '#ffffff',
    color: '#374151',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  topicChipSelected: {
    backgroundColor: '#7c3aed',
    borderColor: '#7c3aed',
    color: '#ffffff',
  },
  inlineNotice: {
    backgroundColor: 'rgba(217,119,6,0.08)',
    border: '1px solid #d97706',
    color: '#92600a',
    fontSize: 13,
    borderRadius: 10,
    padding: '10px 14px',
    marginBottom: 16,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 28px 24px',
    borderTop: '1px solid rgba(10,10,20,0.06)',
    flexShrink: 0,
  },
  navBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    color: '#5c5d70',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '10px 4px',
  },
  primaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    borderRadius: 10,
    border: 'none',
    backgroundColor: '#7c3aed',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginLeft: 'auto',
  },
  primaryBtnDisabled: {
    backgroundColor: '#e5e5ea',
    color: '#9a9bab',
    cursor: 'not-allowed',
  },
};